import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { motion } from "framer-motion";
import { ArrowUpRight, CheckCircle2, Send, ShieldCheck, GraduationCap, Building2, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const AUDIENCES = [
  { v: "institution", l: "I represent an Institution", icon: GraduationCap, register_role: "institution" },
  { v: "organization", l: "I represent an Organization / Employer", icon: Building2, register_role: "organization" },
  { v: "verifier", l: "I'm a Verifier / Background-Check Firm", icon: Search, register_role: "verifier" },
  { v: "general", l: "General inquiry / Other", icon: ShieldCheck, register_role: "professional" },
];

const TEAM_SIZES = ["1–10", "11–50", "51–250", "251–1000", "1000+"];

export default function DemoRequest() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const presetAudience = params.get("audience");
  const initialAudience = AUDIENCES.find((a) => a.v === presetAudience)?.v || "institution";

  const [form, setForm] = useState({
    audience: initialAudience,
    full_name: "",
    email: "",
    phone: "",
    organization_name: "",
    country: "",
    role_title: "",
    team_size: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  useEffect(() => {
    if (presetAudience && AUDIENCES.find((a) => a.v === presetAudience)) {
      setForm((p) => ({ ...p, audience: presetAudience }));
    }
  }, [presetAudience]);

  const audienceMeta = AUDIENCES.find((a) => a.v === form.audience);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.email.trim()) {
      toast.error("Please enter your name and email.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        source_path: window.location.pathname + window.location.search,
        utm: {
          source: params.get("utm_source") || null,
          medium: params.get("utm_medium") || null,
          campaign: params.get("utm_campaign") || null,
        },
      };
      const { data } = await api.post("/inquiries", payload);
      setSubmitted(data);
      toast.success("Thanks! We'll reach out within one business day.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit your inquiry.");
    } finally {
      setLoading(false);
    }
  };

  const goRegister = () => {
    const role = audienceMeta?.register_role || "professional";
    nav(`/register?role=${role}`);
  };

  return (
    <div className="bg-ec-bg text-ec-ink min-h-screen" data-testid="demo-request-page">
      <Navbar />
      <section className="max-w-6xl mx-auto px-6 sm:px-10 py-16 lg:py-20 grid lg:grid-cols-12 gap-10">
        <motion.div
          className="lg:col-span-5"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="inline-flex items-center gap-2 border border-ec-line bg-white px-3 py-1 text-xs eyebrow">
            <Send className="w-3 h-3 text-ec-emerald" />
            Talk to our team
          </div>
          <h1 className="font-display text-5xl sm:text-6xl font-black tracking-tighter mt-5 leading-[0.95]">
            See EthicCred <br />
            in action.
          </h1>
          <p className="text-ec-ink-soft mt-5 text-lg leading-relaxed max-w-md">
            Tell us about your team and we'll set up a personalised walkthrough — usually within one business day.
          </p>
          <ul className="mt-8 space-y-2">
            {[
              "Live walkthrough of the dashboard",
              "Sample passport + verification flow",
              "Pricing tailored to your team size",
              "Compliance & data-residency Q&A",
            ].map((p) => (
              <li key={p} className="flex items-start gap-2 text-sm text-ec-ink">
                <CheckCircle2 className="w-4 h-4 text-ec-emerald mt-0.5" /> {p}
              </li>
            ))}
          </ul>
          <p className="text-xs text-ec-ink-soft mt-8 font-mono-plex">
            Prefer to start solo?{" "}
            <button onClick={goRegister} className="underline text-ec-ink hover:text-ec-emerald" data-testid="demo-register-instead">
              Skip the call — register now →
            </button>
          </p>
        </motion.div>

        <motion.div
          className="lg:col-span-7"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
        >
          {submitted ? (
            <SuccessCard submitted={submitted} audience={form.audience} onRegister={goRegister} />
          ) : (
            <form onSubmit={onSubmit} className="bg-white border border-ec-ink/10 p-6 sm:p-8 noise-overlay space-y-5" data-testid="demo-form">
              <div>
                <Label className="eyebrow">I am a…</Label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2" data-testid="demo-audience-grid">
                  {AUDIENCES.map((a) => {
                    const Icon = a.icon;
                    return (
                      <button key={a.v} type="button" onClick={() => setForm({ ...form, audience: a.v })}
                        aria-pressed={form.audience === a.v}
                        className={`flex flex-col items-center gap-1.5 px-2 py-3 text-xs rounded-sm border transition-colors ${form.audience === a.v ? "bg-ec-ink text-white border-ec-ink" : "border-ec-line hover:bg-ec-mute"}`}
                        data-testid={`demo-audience-${a.v}`}>
                        <Icon className="w-4 h-4" />
                        <span className="text-center leading-tight">{a.l.replace(/^I .*?(an? )?/, "")}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <FieldEl label="Full Name" required>
                  <Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} required className="rounded-sm" data-testid="demo-full-name" />
                </FieldEl>
                <FieldEl label="Work Email" required>
                  <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="rounded-sm" data-testid="demo-email" />
                </FieldEl>
                <FieldEl label="Phone">
                  <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-sm" placeholder="+91 98765 43210" data-testid="demo-phone" />
                </FieldEl>
                <FieldEl label="Country">
                  <Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="rounded-sm" data-testid="demo-country" />
                </FieldEl>
              </div>

              {(form.audience === "institution" || form.audience === "organization") && (
                <div className="grid sm:grid-cols-2 gap-4">
                  <FieldEl label={form.audience === "institution" ? "Institution name" : "Organization name"} required>
                    <Input value={form.organization_name} onChange={(e) => setForm({ ...form, organization_name: e.target.value })} required className="rounded-sm" data-testid="demo-org-name" />
                  </FieldEl>
                  <FieldEl label="Your role">
                    <Input value={form.role_title} onChange={(e) => setForm({ ...form, role_title: e.target.value })} className="rounded-sm" placeholder="e.g. Head of Placements" data-testid="demo-role-title" />
                  </FieldEl>
                  <FieldEl label={form.audience === "institution" ? "Student strength" : "Team size"}>
                    <Select value={form.team_size} onValueChange={(v) => setForm({ ...form, team_size: v })}>
                      <SelectTrigger className="rounded-sm" data-testid="demo-team-size-trigger"><SelectValue placeholder="Select range" /></SelectTrigger>
                      <SelectContent>
                        {TEAM_SIZES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </FieldEl>
                </div>
              )}

              <FieldEl label="How can we help?">
                <Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={4} className="rounded-sm" placeholder="Tell us about your placement goals, compliance needs, or anything else." data-testid="demo-message" />
              </FieldEl>

              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-ec-line pt-5">
                <p className="text-[11px] text-ec-ink-soft font-mono-plex max-w-sm">
                  By submitting you agree to our <Link to="/legal/privacy" className="underline">Privacy Policy</Link>. We'll only use this to contact you.
                </p>
                <Button type="submit" disabled={loading} className="rounded-sm bg-ec-ink hover:bg-zinc-800 px-6 h-11 gap-2" data-testid="demo-submit">
                  {loading ? "Sending…" : "Request Demo"} <ArrowUpRight className="w-4 h-4" />
                </Button>
              </div>
            </form>
          )}
        </motion.div>
      </section>
      <Footer />
    </div>
  );
}

function FieldEl({ label, required, children }) {
  return (
    <div>
      <Label className="eyebrow">{label} {required && <span className="text-ec-emerald">*</span>}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function SuccessCard({ submitted, audience, onRegister }) {
  return (
    <div className="bg-white border border-ec-emerald/30 bg-ec-emerald/5 p-8 sm:p-12 noise-overlay" data-testid="demo-success-card">
      <CheckCircle2 className="w-12 h-12 text-ec-emerald" />
      <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tight mt-4">Thanks — we've got it.</h2>
      <p className="text-ec-ink-soft mt-3 max-w-md">
        Our team will reach out within one business day on the email you provided.
        Your ticket reference is <span className="font-mono-plex font-bold text-ec-ink">#{submitted.id?.slice(0, 8).toUpperCase()}</span>.
      </p>
      <div className="mt-6 flex flex-wrap gap-3">
        <Button onClick={onRegister} className="rounded-sm bg-ec-ink hover:bg-zinc-800 gap-2" data-testid="demo-success-register">
          Get a head start — register now <ArrowUpRight className="w-4 h-4" />
        </Button>
        <Link to="/"><Button variant="outline" className="rounded-sm" data-testid="demo-success-home">Back to home</Button></Link>
      </div>
    </div>
  );
}
